import Icons from "@/components/icons";
import type { SchemaBasedDataProvider } from "@/features/integrations/modules/providers/types";
import type {
  ModuleConfigurationSchema,
  UiComponent,
} from "@/features/integrations/types";

// Default action configuration for getAvailableActions (legacy)
const DEFAULT_ACTION_CONFIG = {
  variant: "default" as const,
  size: "sm" as const,
  icon: Icons.SyncIcon as React.ComponentType<{
    fontSize?: number;
    className?: string;
  }>,
};
// example: "jobSync" | "organic"
export type SchemaBasedProviderModuleSubKey = string;

/**
 * Creates a schema-based data provider. Returns UiComponent[] directly from schema.ui
 * (no props→config mapping). Pages use renderComponent(component, context) with these components.
 */
export function createSchemaBasedProvider(
  integrationName: string,
  schema: ModuleConfigurationSchema | undefined,
  moduleSubKey: SchemaBasedProviderModuleSubKey,
): SchemaBasedDataProvider {
  const ui: UiComponent[] = schema?.ui ?? [];
  const normalizedIntegrationName = integrationName.toLowerCase();
  const moduleKey: SchemaBasedProviderModuleSubKey = moduleSubKey;

  const tableToolbar = ui.find((c) => c.component === "tableToolbar");
  const actionsFromToolbar = (tableToolbar?.props?.actions ??
    []) as UiComponent[];

  return {
    getIntegrationName: () => normalizedIntegrationName,

    getTranslationNamespace: () =>
      `integrations.${normalizedIntegrationName}.${moduleKey}`,

    getPageTitleKey: () =>
      `integrations.${normalizedIntegrationName}.${moduleKey}.clientsOverview.title`,

    getPageSubtitleKey: () =>
      `integrations.${normalizedIntegrationName}.${moduleKey}.clientsOverview.subtitle`,

    getClientDetailPageTitleKey: () => {
      // backwards compatibility for jobSync
      if (moduleKey === "jobSync" && normalizedIntegrationName === "indeed") {
        return `integrations.${normalizedIntegrationName}.jobSync.syncOverview.title`;
      }
      return `integrations.${normalizedIntegrationName}.${moduleKey}.clientDetail.title`;
    },

    getClientDetailPageSubtitleKey: () =>
      `integrations.${normalizedIntegrationName}.${moduleKey}.clientDetail.subtitle`,

    getTenantViews: () => ui,

    getClientViews: () => ui,

    getAvailableActions: () =>
      actionsFromToolbar.map((action) => {
        const props = action.props ?? {};
        const actionConfig = props.action as { actionKey?: string } | undefined;
        return {
          key: actionConfig?.actionKey ?? "",
          label: (props.label as string) ?? "",
          variant: DEFAULT_ACTION_CONFIG.variant,
          size: DEFAULT_ACTION_CONFIG.size,
          icon: DEFAULT_ACTION_CONFIG.icon,
          onClick: async () => {
            // Handled by executeAction from getActionHandlers
          },
        };
      }),
  };
}
